import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { BottomDrawer } from '../../BottomDrawer';
import { Button, Modal } from '../../_custom';
import {
    PlateEditor,
    PreviewButton,
    exportEditorToPdfServer,
    exportEditorToDocxFile,
    ProgressBorder,
    ExportingLabel,
    useExportProgress,
    slateToPlateValue,
    isSlateShape,
    normalizeTableColSizesInValue,
} from '../../PlateEditor';
import { usePnoReportLogoResolver, usePnoReportLogoBand } from '../../Reports/usePnoReportLogo';
import { useDocumentHistory } from '../../HistorySideDrawer/useDocumentHistory';
import HistorySideDrawer from '../../HistorySideDrawer/HistorySideDrawer';
import LoadingSpinner from '../../_custom/LoadingSpinner/LoadingSpinner';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { ClockCounterDark, PURPLE_DROPDOWN_CARET, regenerateIcon, commentsIcon, editIconPurple } from '../../../assets/hostedassets';
import { processImagesBeforeSave, fetchImageUrlsBulk, collectDocIdsFromSlate } from '../../PlateEditor/services/imageUploadService';
import {
    closeCommentsThread,
    resetAndHideCommentsThread,
    setShowCommentsThread,
} from '../../../store/commentsThread/commentsThread_actions';
import { COMMENTS_THREAD_SOURCES } from '../../../store/commentsThread/commentsThread_reducer';
import { DATE_FORMATS, formatDateByFormat, getTimeStampForFileName } from '../../../utils/dateUtils';
import { Mixpanel } from '../../../utils/mixpanel';
import { getSubdomain } from '../../../utils/utils';
import { CREATE_DEAL } from '../../../utils/constants/commonConstants';
import {
    getSignedUrl,
    saveDocMetaDataInDocService,
    uploadFile,
} from '../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import {
    AICA_ONEPAGER_CLOSED,
    AICA_ONEPAGER_VIEW_HISTORY,
    AICA_ONEPAGER_RESTORE_VERSION,
} from '../../../utils/constants/mixpanelEvents/investorEvents';
import styles from './DealSummaryEditorPlate.module.scss';
import { COMMENTS_PORTAL_TARGET_ID, HISTORY_PORTAL_TARGET_ID, PLATE_TOOLBAR_HOST_ID } from './dealSummaryEditorConstants';
import { useBeforeUnloadGuard } from './useBeforeUnloadGuard';
import { useFeatureVisibility } from '../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../utils/constants/visibilityKeys';

interface DealSummaryEditorPlateProps {
    openModal: boolean;
    closeModal: () => void;
    editorInitialData: any;
    editorReady: boolean;
    generating?: boolean;
    companyNameProp?: string;
    userInfo?: { userName?: string; onePagerData?: { lastUpdatedAt?: string | number; title?: string } } | Record<string, any>;
    assesseeOrgId?: string;
    saveOnePagerTemplateDataForOrg?: (content: any, keepDrawerOpen?: boolean, onSuccess?: () => void, isDraft?: boolean) => void;
    /**
     * Direct commit for the history flows (pre-flight save + restore) — bypasses
     * the parent save's re-entrancy guard, which silently drops a concurrent save
     * (and never resolves, hanging the restore's reload + re-select).
     */
    persistCommittedVersion?: (content: any[]) => Promise<void>;
    /** Lineage id getter — read at fetch time; the genId is minted on first save. */
    getDocumentGenId?: () => string | undefined;
    /**
     * Notified when the user commits an in-editor title edit — the parent
     * persists it via the save payload's `name` (a title edit alone never
     * marks content dirty, so the parent also tracks it for rename-only saves).
     */
    onTitleChange?: (title: string) => void;
    setShowRegenConfirmModal?: (open: boolean) => void;
    source?: string;
    categoryId?: number;
    flowId?: number;
    metadataUpdate?: (fileInfo: { url?: string; fileName?: string; docId?: string | number }) => void;
}

function toMs(v: string | number | undefined): number | undefined {
    if (!v) return undefined;
    const ms = typeof v === 'number' ? v : dayjs(v).valueOf();
    return Number.isFinite(ms) ? ms : undefined;
}

// Recursive word count over a Plate/Slate value tree. Walks every leaf's
// `text` field and tallies whitespace-separated runs. Cheap (single pass,
// no allocations beyond split), but we still throttle the wrapper that
// calls this so it doesn't run on every keystroke.
function countWords(nodes: any): number {
    if (!Array.isArray(nodes)) return 0;
    let count = 0;
    const stack: any[] = [...nodes];
    while (stack.length > 0) {
        const node = stack.pop();
        if (!node) continue;
        if (typeof node.text === 'string') {
            const trimmed = node.text.trim();
            if (trimmed) count += trimmed.split(/\s+/).length;
        }
        if (Array.isArray(node.children)) {
            for (const child of node.children) stack.push(child);
        }
    }
    return count;
}

// Debounce window for autosaving the live draft after the user stops typing.
const AUTOSAVE_DEBOUNCE_MS = 1000;
// Word count is recomputed on blur (instant) and also on a longer idle timer
// so an active typist still sees a refreshed count after a pause. We do NOT
// run it on the typing hot-path (the old 500ms throttle fired countWords on
// every burst, walking the full tree during active editing).
const WORD_COUNT_IDLE_MS = 2000;
// Minimum time the "Saving" status stays visible so it doesn't flash on fast saves.
const SAVING_STATUS_MIN_VISIBLE_MS = 1500;

function buildDefaultTitle(companyName?: string, timestamp?: string | number): string {
    const d = timestamp ? dayjs(timestamp) : dayjs();
    const prefix = companyName ? `${companyName} ` : '';
    return `${prefix}One Pager (${d.format('DD MMM YYYY')})`;
}

export default function DealSummaryEditorPlate({
    openModal,
    closeModal,
    editorInitialData,
    editorReady,
    generating,
    companyNameProp,
    userInfo,
    assesseeOrgId,
    saveOnePagerTemplateDataForOrg,
    persistCommittedVersion,
    getDocumentGenId,
    onTitleChange,
    setShowRegenConfirmModal,
    source,
    categoryId,
    flowId,
    metadataUpdate,
}: DealSummaryEditorPlateProps) {
    const dispatch = useAppDispatch();
    const canvasRef = useRef<HTMLDivElement>(null);
    const exportProgress = useExportProgress();
    // Derived so the existing `disabled={… || exporting}` guards keep working.
    const exporting = exportProgress.isRunning;
    const [saving, setSaving] = useState(false);
    const [autosaveStatus, setAutosaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
    const [currentEditorData, setCurrentEditorData] = useState<any>(editorInitialData);
    const attachingRef = useRef(false);
    const [attachingToDeal, setAttachingToDeal] = useState(false);
    // True for the duration of a Save & Close commit. Blocks any autosave that
    // was already scheduled/in-flight from landing AFTER the commit and inserting
    // a stray draft the BE then reloads instead of the committed version.
    const isCommittingRef = useRef(false);

    const user = useAppSelector((state) => state.user.userData);
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const companyName = companyNameProp || '';

    const commentsThreadState = useAppSelector((state) => state.commentsThreadReducer);
    const commentsActive = Boolean(commentsThreadState?.showCommentsThread && !commentsThreadState?.closingCommentsThread);
    const commentsExpanded = commentsActive && Boolean(commentsThreadState?.expandCommentsThread);

    // AI features (⌘J prompt menu + toolbar AI button) are gated by the
    // `AI.enabled` visibility flag. Missing-key / disabled = no AI surface,
    // even if `onePager.v2` is on — the two flags are independent so ops can
    // ship the new editor without AI to specific PNOs.
    const { aiEnabled } = useFeatureVisibility({
        aiEnabled: VISIBILITY_KEYS.AI.ENABLED,
    });

    // ── Title / inline rename ────────────────────────────────────────────────
    // Title pulls from userInfo if the backend gave us one, otherwise falls
    // back to the dated default (matches the Figma "One Pager (DD MMM YYYY)").
    // Edits are LOCAL ONLY today — there's no name field in the save payload.
    // When backend support lands, plumb the new name through the save payload.
    const onePagerInfo = (userInfo as any)?.onePagerData;
    const onePagerTitle: string | undefined = onePagerInfo?.title;
    const onePagerLastUpdatedAt: string | number | undefined = onePagerInfo?.lastUpdatedAt;
    // The last-editor name lives on the nested onePagerData (same object as
    // lastUpdatedAt), not at the top level — read it there first so the footer's
    // "… by {name}" isn't blank. Fall back across the shapes this legacy endpoint
    // can return (nested userName/lastUpdatedBy/createdBy, then the top level).
    // `||` (not `??`): an empty-string field must fall through too, otherwise a
    // blank `userName` would stop the chain and reproduce the empty "by " footer.
    const onePagerUserName: string | undefined =
        onePagerInfo?.userName ||
        onePagerInfo?.lastUpdatedBy ||
        onePagerInfo?.createdBy ||
        (userInfo as any)?.userName ||
        (userInfo as any)?.lastUpdatedBy ||
        undefined;

    const initialTitle = onePagerTitle || buildDefaultTitle(companyName, onePagerLastUpdatedAt);
    const [title, setTitle] = useState<string>(initialTitle);
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState<string>(initialTitle);
    const titleInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        // Refresh the title when the loaded template data finally arrives.
        const next = onePagerTitle || buildDefaultTitle(companyName, onePagerLastUpdatedAt);
        setTitle(next);
        setTitleDraft(next);
    }, [onePagerTitle, onePagerLastUpdatedAt, companyName]);

    useEffect(() => {
        if (editingTitle) {
            // Defer focus a tick so React has actually mounted the input.
            setTimeout(() => titleInputRef.current?.select(), 0);
        }
    }, [editingTitle]);

    const startTitleEdit = () => {
        setTitleDraft(title);
        setEditingTitle(true);
    };
    const commitTitleEdit = () => {
        const next = titleDraft.trim() || title;
        setTitle(next);
        setEditingTitle(false);
        // Persisting the rename is the parent's job (save payload `name`) —
        // only notify on an actual change so an untouched blur stays local.
        if (next !== title) onTitleChange?.(next);
    };
    const cancelTitleEdit = () => {
        setTitleDraft(title);
        setEditingTitle(false);
    };

    // ── Editor content tracking ──────────────────────────────────────────────
    // Wire format: PLATE going forward. Legacy Slate docs are detected at load
    // (isSlateShape) and converted exactly once on the way in. Every save after
    // that writes Plate JSON directly.
    const editorContentRef = useRef<any[]>(
        Array.isArray(editorInitialData)
            ? (normalizeTableColSizesInValue(
                  isSlateShape(editorInitialData) ? (slateToPlateValue(editorInitialData) as any[]) : editorInitialData,
              ) as any[])
            : [],
    );
    const originalDocIdsRef = useRef<string[]>(Array.isArray(editorInitialData) ? collectDocIdsFromSlate(editorInitialData) : []);

    // Last-edit display: starts from server data, then advances locally after
    // each successful save so the chrome stays in sync without re-fetching.
    const [lastSavedAt, setLastSavedAt] = useState<number | undefined>(() => toMs(onePagerLastUpdatedAt));
    const [lastSavedBy, setLastSavedBy] = useState<string | undefined>(onePagerUserName);

    useEffect(() => {
        const ms = toMs(onePagerLastUpdatedAt);
        if (ms !== undefined) setLastSavedAt(ms);
        if (onePagerUserName) setLastSavedBy(onePagerUserName);
    }, [onePagerLastUpdatedAt, onePagerUserName]);

    useEffect(() => {
        setCurrentEditorData(editorInitialData);
        if (!Array.isArray(editorInitialData)) return;
        const docIds = collectDocIdsFromSlate(editorInitialData);
        originalDocIdsRef.current = docIds;
        if (docIds.length > 0) {
            fetchImageUrlsBulk(docIds).catch(() => {
                /* cache warm is best-effort */
            });
        }
    }, [editorInitialData]);

    // Word count drives the footer text. We update it on a throttle so
    // typing doesn't re-render the footer on every keystroke.
    const [wordCount, setWordCount] = useState<number>(() => countWords(editorInitialData));
    const wordCountTimerRef = useRef<number | null>(null);

    const saveWithImageUploads = useCallback(
        (content: any[], keepDrawerOpen: boolean | undefined, onSuccess?: () => void, isDraft = false) => {
            if (!saveOnePagerTemplateDataForOrg) return onSuccess?.();
            // Always externalise base64 images (upload → docId) BEFORE persisting —
            // including on autosave — so a draft never carries base64 into Mongo.
            processImagesBeforeSave(content, originalDocIdsRef.current, 'one-pager')
                .then((processed) => {
                    originalDocIdsRef.current = collectDocIdsFromSlate(processed);
                    saveOnePagerTemplateDataForOrg(
                        processed,
                        Boolean(keepDrawerOpen),
                        () => {
                            dirtyRef.current = false;
                            onSuccess?.();
                        },
                        isDraft,
                    );
                })
                .catch(() => {
                    saveOnePagerTemplateDataForOrg(content, Boolean(keepDrawerOpen), onSuccess, isDraft);
                });
        },
        [saveOnePagerTemplateDataForOrg],
    );

    // History/restore saves go through the parent's direct commit (no
    // re-entrancy guard — see persistCommittedVersion prop). Image processing
    // stays here, next to the doc-id baseline it maintains.
    const persistVersion = useCallback(
        async (content: any[]) => {
            if (!persistCommittedVersion) return;
            const processedContent = await processImagesBeforeSave(content, originalDocIdsRef.current, 'one-pager');
            await persistCommittedVersion(processedContent);
            originalDocIdsRef.current = collectDocIdsFromSlate(processedContent);
            dirtyRef.current = false;
        },
        [persistCommittedVersion],
    );

    const dirtyRef = useRef(false);
    useBeforeUnloadGuard(dirtyRef);

    // ── Mount-flush guard ────────────────────────────────────────────────────
    // Plate's PlateValueWatcher dispatches onChange (via Promise.resolve().then)
    // on EVERY PlateEditor mount — including after a history key bump where the
    // user made zero edits. Without this guard, dirtyRef flips true the moment
    // any version is previewed, causing:
    //   a) a redundant background save on every X-close after history is opened
    //   b) useBeforeUnloadGuard warning the user on tab-close even though they
    //      only looked at history and changed nothing
    //
    // Fix: when editorKey changes (key bump = editor remounting with new content),
    // mark the NEXT onChange call as a mount-flush and skip the dirty flag for it.
    // The deferred microtask from PlateValueWatcher always fires AFTER both
    // useEffect callbacks complete, so this ref is reliably set before the skip.
    const mountFlushRef = useRef(true); // true on first mount too — initial load isn't dirty

    // ── Autosave (live draft) ────────────────────────────────────────────────
    // Debounced background persist of the in-progress draft (isDraft = true),
    // fired AUTOSAVE_DEBOUNCE_MS after the user stops typing. The draft is a
    // single mutable row per document lineage (genId); Save later flips it to a
    // committed version. autosaveFnRef always holds the latest closure so the
    // stable scheduler never captures a stale one.
    const autosaveTimerRef = useRef<number | null>(null);
    const autosaveStatusResetRef = useRef<number | null>(null);
    const savingStartRef = useRef<number>(0);
    const autosaveFnRef = useRef<() => void>(() => {});
    const scheduleAutosave = useCallback(() => {
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = window.setTimeout(() => autosaveFnRef.current(), AUTOSAVE_DEBOUNCE_MS);
    }, []);

    // Cancel the pending word-count timer on unmount so a stale timer
    // doesn't setState against a torn-down component.
    useEffect(() => {
        return () => {
            if (wordCountTimerRef.current) window.clearTimeout(wordCountTimerRef.current);
        };
    }, []);

    const handlePlateChange = useCallback(
        (plateValue: any) => {
            editorContentRef.current = plateValue;
            // Skip dirty-marking for the initialization flush that PlateValueWatcher
            // fires on every PlateEditor mount. Only real user edits should mark dirty.
            if (mountFlushRef.current) {
                mountFlushRef.current = false;
                return;
            }
            dirtyRef.current = true;
            // Schedule a debounced draft autosave on every real edit.
            scheduleAutosave();
            // Arm the idle-timer so a long pause during active typing still refreshes
            // the word count. The blur handler recomputes immediately on focus-loss,
            // so the timer is the only mechanism needed while typing.
            if (wordCountTimerRef.current) window.clearTimeout(wordCountTimerRef.current);
            wordCountTimerRef.current = window.setTimeout(() => {
                setWordCount(countWords(editorContentRef.current));
            }, WORD_COUNT_IDLE_MS);
        },
        [scheduleAutosave],
    );

    // Resolve the content to save: prefer the live editor ref, fall back to the
    // last loaded data. Shared by autosave, save, and close-with-save.
    const resolveEditorContent = useCallback(
        () => (editorContentRef.current?.length ? editorContentRef.current : Array.isArray(currentEditorData) ? currentEditorData : []),
        [currentEditorData],
    );

    // The actual draft autosave. Kept in a ref (synced below) so `scheduleAutosave`
    // can stay dependency-free while always running the freshest closure.
    const runAutosave = useCallback(() => {
        if (isCommittingRef.current) return; // a commit is in progress — never write a competing draft
        if (source === CREATE_DEAL) return; // CREATE_DEAL attaches a PDF, not a template draft
        if (!dirtyRef.current) return;
        const content = resolveEditorContent();
        if (!content.length) return;
        savingStartRef.current = Date.now();
        setAutosaveStatus('saving');
        saveWithImageUploads(
            content,
            true, // keepDrawerOpen — autosave never closes (isDraft also prevents close)
            () => {
                setLastSavedAt(Date.now());
                // Keep "Saving" visible for a minimum window so it doesn't flash.
                const elapsed = Date.now() - savingStartRef.current;
                const delay = Math.max(0, SAVING_STATUS_MIN_VISIBLE_MS - elapsed);
                if (autosaveStatusResetRef.current) window.clearTimeout(autosaveStatusResetRef.current);
                autosaveStatusResetRef.current = window.setTimeout(() => {
                    setAutosaveStatus('saved');
                }, delay);
            },
            true, // isDraft → upsert the live draft
        );
    }, [source, resolveEditorContent, saveWithImageUploads]);

    useEffect(() => {
        autosaveFnRef.current = runAutosave;
    }, [runAutosave]);

    useEffect(
        () => () => {
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
            if (autosaveStatusResetRef.current) window.clearTimeout(autosaveStatusResetRef.current);
        },
        [],
    );

    const handleLoadVersion = useCallback((sanitizedSlate: any[]) => {
        setCurrentEditorData(sanitizedSlate);
        // Eagerly sync both refs so saves triggered before the PlateEditor
        // remount fires handlePlateChange (e.g. a fast X-close) use the
        // restored content, not the pre-restore content.
        editorContentRef.current = sanitizedSlate;
        // Reset the image doc-ID baseline to the restored version so
        // processImagesBeforeSave doesn't re-upload images that already
        // exist in the restored doc, or miss new ones from the old version.
        originalDocIdsRef.current = collectDocIdsFromSlate(sanitizedSlate);
        // Refresh the count immediately to reflect the loaded version.
        setWordCount(countWords(sanitizedSlate));
    }, []);

    const getMixpanelProperties = useCallback(
        () => ({
            currentUrl: window.location.href,
            pnoId: domainMetadata?.investorOrgId,
            orgId: assesseeOrgId,
            userEmail: user?.primaryEmail,
        }),
        [domainMetadata?.investorOrgId, assesseeOrgId, user?.primaryEmail],
    );

    const history = useDocumentHistory({
        documentType: 'ONE_PAGER',
        assesseeOrgId: assesseeOrgId || '',
        getGenId: () => getDocumentGenId?.(),
        editorContentRef,
        onLoadVersion: handleLoadVersion,
        persistVersion,
        onHistoryViewed: () => Mixpanel.track(AICA_ONEPAGER_VIEW_HISTORY, getMixpanelProperties()),
        enabled: Boolean(user?.investorOrgId && assesseeOrgId && saveOnePagerTemplateDataForOrg && persistCommittedVersion),
        // The history drawer's pre-flight save (see useDocumentHistory.handleToggleHistory)
        // persists the same content this debounced timer would autosave. Cancel the
        // pending timer so it can't fire mid-flight and race the pre-flight save.
        onBeforeHistoryPreflightSave: () => {
            if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        },
    });

    // Key-change effect — placed AFTER `history` is declared so the dep is valid.
    // When editorKey changes (PlateEditor remounts with a different version),
    // flag the next onChange as a mount-flush and reset dirty to false so the
    // browser-unload guard doesn't fire and a redundant save doesn't trigger.
    useEffect(() => {
        mountFlushRef.current = true;
        dirtyRef.current = false;
    }, [history.editorKey]);

    // Snapshot the initially-loaded content as "saved" so opening the history
    // drawer without edits doesn't fire a redundant pre-flight save (which would
    // create a duplicate version on every open). markAsSaved is stable
    // (useCallback []), so this runs at most once thanks to the ref guard.
    const didMarkInitialSavedRef = useRef(false);
    useEffect(() => {
        if (didMarkInitialSavedRef.current) return;
        if (editorReady && Array.isArray(editorContentRef.current) && editorContentRef.current.length) {
            history.markAsSaved(editorContentRef.current);
            didMarkInitialSavedRef.current = true;
        }
    }, [editorReady, history.markAsSaved]);

    // Restore-confirm modal — same flow as the Slate editor. The drawer's
    // internal three-dot menu also calls onRestoreConfirm directly; this modal
    // is the explicit footer "Restore this version" path.
    const [showExternalRestoreModal, setShowExternalRestoreModal] = useState(false);

    const handleRestoreClick = () => {
        setShowExternalRestoreModal(true);
    };

    const handleConfirmExternalRestore = () => {
        const selectedEntry = history.historyEntries.find((entry) => entry._id === history.selectedHistoryEntryId);
        if (!selectedEntry) {
            // History entries may have been cleared between button click and
            // confirm — close the modal and tell the user.
            setShowExternalRestoreModal(false);
            toast.error('Could not restore — history entry no longer available. Please reopen history and try again.');
            return;
        }
        Mixpanel.track(AICA_ONEPAGER_RESTORE_VERSION, getMixpanelProperties());
        history.selectHistoryEntry(selectedEntry);
        setShowExternalRestoreModal(false);
    };

    const handleRestoreConfirmTracking = useCallback(() => {
        Mixpanel.track(AICA_ONEPAGER_RESTORE_VERSION, getMixpanelProperties());
    }, [getMixpanelProperties]);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Dual-format reader. Convert Slate-shape (legacy stored docs) to Plate
    // exactly once at mount. Already-Plate documents pass through untouched.
    // Saves always emit Plate going forward (handlePlateChange stores the
    // Plate value directly in editorContentRef), so any doc that's touched
    // after the deploy organically migrates on the next save.
    const initialValue = useMemo(() => {
        if (!Array.isArray(currentEditorData) || currentEditorData.length === 0) return undefined;
        const plate = isSlateShape(currentEditorData) ? slateToPlateValue(currentEditorData) : currentEditorData;
        return normalizeTableColSizesInValue(plate) as any;
    }, [currentEditorData]);

    const isLoading = generating || !editorReady;

    const toggleComments = useCallback(() => {
        if (commentsActive) {
            dispatch(closeCommentsThread());
            return;
        }
        if (!assesseeOrgId) return;
        dispatch(
            setShowCommentsThread({
                source: COMMENTS_THREAD_SOURCES.ONE_PAGER,
                assesseeOrgId,
            }),
        );
    }, [commentsActive, assesseeOrgId, dispatch]);

    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef<HTMLDivElement>(null);

    // Close export menu on outside click / Escape
    useEffect(() => {
        if (!exportMenuOpen) return;
        const onDocClick = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setExportMenuOpen(false);
            }
        };
        // Capture-phase + stopPropagation: BottomDrawer's own Esc→close is a
        // document BUBBLE listener registered at drawer open, so it always
        // beats a bubble listener added here later — Esc meant to dismiss this
        // menu would close the whole drawer. Document capture runs first.
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setExportMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey, true);
        };
    }, [exportMenuOpen]);

    const resolvePnoLogo = usePnoReportLogoResolver();
    // RECUR-27604: always a One Pager — no doctype gate needed here (unlike
    // PlateDocumentDrawer's shared multi-doctype surface). The overlay must
    // reserve the SAME per-page space + logo URL (phase 4) the export itself uses.
    const { reservedPx: logoReservedPx, logoUrl: pnoLogoUrl, editorBandEnabled: logoEditorBandEnabled } = usePnoReportLogoBand();

    const safeExportName = () => (title || 'deal-summary').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_');

    const handleExportDocx = async () => {
        if (exporting) return;
        setExportMenuOpen(false);
        await exportProgress.track(() => exportEditorToDocxFile({ value: editorContentRef.current ?? [], filename: safeExportName() }));
    };

    const handleExportServer = async () => {
        if (!canvasRef.current || exporting) return;
        setExportMenuOpen(false);
        try {
            const pnoLogoDataUrl = await resolvePnoLogo();
            await exportProgress.track(() =>
                exportEditorToPdfServer({
                    target: canvasRef.current!,
                    filename: safeExportName(),
                    injectModelPageBreaks: true,
                    pnoLogoDataUrl,
                }),
            );
        } catch {
            toast.error('PDF export failed. Please try again.');
        }
    };

    const handleRegenerate = () => {
        setShowRegenConfirmModal?.(true);
    };

    const generateAndAttachOnePager = useCallback(async () => {
        if (!canvasRef.current) return false;
        if (!assesseeOrgId || !categoryId || !flowId) return false;
        try {
            const exportFilename = (title || 'deal-summary').replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_');
            // RECUR-27604: the attached/emailed one-pager must carry the same
            // PNO logo header as the interactively-downloaded one.
            const pnoLogoDataUrl = await resolvePnoLogo();
            // Server-side vector PDF (sharper, paginated, text-selectable) for the
            // attached/emailed one-pager. exportEditorToPdfServer falls back to the
            // raster exporter on a backend failure, so a Blob is always produced. The
            // attachment contract downstream is the mnemosyne docId minted below — the
            // email resolves that docId to bytes — so page count / size don't matter.
            const blob = (await exportEditorToPdfServer({
                target: canvasRef.current,
                filename: exportFilename,
                returnBlob: true,
                injectModelPageBreaks: true,
                pnoLogoDataUrl,
            })) as Blob | undefined;
            if (!blob) return false;

            const fileName = `${companyName ? companyName + '-' : ''} Brief Profile - ${getTimeStampForFileName()}.pdf`;
            const file = new File([blob], fileName, { type: 'application/pdf' });

            const uploadConfig = {
                assesseeOrgId,
                categoryId,
                mnemosyneOrgId: domainMetadata?.investorOrgId,
            };
            const fileData = {
                file: {
                    name: fileName,
                    type: 'application/pdf',
                    size: file.size,
                },
            };

            const signResponse = await getSignedUrl(fileData, uploadConfig);
            const [uploadResponse, docData] = await Promise.all([
                uploadFile(file, signResponse.signedUrl),
                saveDocMetaDataInDocService({
                    input: {
                        categoryId,
                        mnemosyneOrgId: domainMetadata?.investorOrgId,
                        fileName: fileData.file.name,
                        fileSizeKb: fileData.file.size / 1_000,
                        flowId,
                        fileType: fileData.file.type,
                    },
                    orgId: assesseeOrgId,
                }),
            ]);

            if (!uploadResponse?.url || !docData?.id) {
                throw new Error(uploadResponse?.url ? 'One-pager metadata save failed' : 'One-pager upload failed (no URL returned)');
            }

            metadataUpdate?.({
                url: uploadResponse.url,
                fileName: fileData.file.name,
                docId: docData.id,
            });
            toast.success('Successfully Saved');
            return true;
        } catch (err: any) {
            toast.error(`Error saving PDF: ${err?.message || 'Unknown error'}`);
            return false;
        }
        // resolvePnoLogo's identity is already stable (usePnoReportLogoResolver's
        // own useCallback), so adding it here adds no re-render churn.
    }, [assesseeOrgId, categoryId, flowId, companyName, title, domainMetadata?.investorOrgId, metadataUpdate, resolvePnoLogo]);

    const attachThenClose = useCallback(() => {
        if (attachingRef.current) return;
        attachingRef.current = true;
        setAttachingToDeal(true);
        setAutosaveStatus('saving');
        generateAndAttachOnePager()
            .then((attached) => {
                if (attached) closeModal();
            })
            .finally(() => {
                attachingRef.current = false;
                setAttachingToDeal(false);
                // Reset the indicator so it doesn't stay on "Saving…"
                // forever when the attach fails (drawer stays open on failure).
                setAutosaveStatus('idle');
            });
    }, [generateAndAttachOnePager, closeModal]);

    const handleCloseWithSave = () => {
        // A Save & Close commit is still in flight — closing now would let this
        // X-close start a second, concurrent attachThenClose() that can resolve
        // (and unmount the drawer) before the original commit does, so a later
        // failure of that commit leaves a PDF attached with content that was
        // never actually saved. No-op until the in-flight commit settles.
        if (isCommittingRef.current || saving) return;
        // Cross-icon close does NOT commit — it leaves the live draft untouched
        // (isDraft stays true), so "just closing" never creates a committed
        // version. Flush one final DRAFT autosave in the background (the save
        // lives in the parent OnePager, which stays mounted) so the last few
        // keystrokes within the debounce window aren't lost, then close.
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        const content = resolveEditorContent();
        if (source !== CREATE_DEAL && dirtyRef.current && content.length) {
            // keepDrawerOpen=true so the parent doesn't dispatch a close; we close
            // ourselves below. isDraft=true → persist as the draft, do NOT commit.
            saveWithImageUploads(content, true, () => {}, true);
        }
        Mixpanel.track(AICA_ONEPAGER_CLOSED, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: domainMetadata?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: assesseeOrgId,
        });

        if (source === CREATE_DEAL) {
            attachThenClose();
            return;
        }

        closeModal();
    };

    const handleSaveAndClose = () => {
        if (saving || attachingRef.current) return;
        // Previewing a historical version: editorContentRef holds that OLD content,
        // so Save & Close must not silently commit it over the live doc. Restoring
        // goes through the explicit "Restore this version" flow instead. (The button
        // is also disabled in this state — this is the defense-in-depth guard.)
        if (history.isOpen) return;
        if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
        const content = resolveEditorContent();
        if (!content.length) return;
        setSaving(true);
        isCommittingRef.current = true;
        // keepDrawerOpen=false → the parent commits the draft to a version, then
        // dispatches closeOnePager() to close the drawer — matching "Save & Close"
        // on every other document editor. isDraft=false commits (not a draft).
        // RCPI-10872: for CREATE_DEAL, keepDrawerOpen must be TRUE instead — the
        // parent's own closeOnePager() would otherwise race attachThenClose()
        // below (both fire from this same onSuccess tick), closing the drawer
        // and unmounting it before generateAndAttachOnePager's PDF upload
        // finishes. attachThenClose() must be the SOLE closer for CREATE_DEAL,
        // via its own closeModal() once the attach resolves.
        saveWithImageUploads(
            content,
            source === CREATE_DEAL,
            () => {
                setSaving(false);
                isCommittingRef.current = false;
                if (source === CREATE_DEAL) attachThenClose();
            },
            false,
        );
    };

    // ── Header (left): title + edit pill (last edit / regenerate moved to footer) ──
    const formattedLastSaved = lastSavedAt ? formatDateByFormat(lastSavedAt, DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY) : null;

    const headerTitle = (
        <div className={styles.headerLeft}>
            <div className={styles.titleBlock}>
                {editingTitle ? (
                    <input
                        ref={titleInputRef}
                        className={styles.titleInput}
                        value={titleDraft}
                        onChange={(e) => setTitleDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                commitTitleEdit();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelTitleEdit();
                            }
                        }}
                        onBlur={commitTitleEdit}
                        aria-label="One pager title"
                    />
                ) : (
                    <>
                        <span className={styles.titleText} title={title}>
                            {title}
                        </span>
                        <div className={styles.editPill} onClick={startTitleEdit} aria-label="Rename one pager" title="Rename">
                            <img src={editIconPurple} alt="" aria-hidden className={styles.editPillIcon} />
                            <span>Edit</span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );

    const headerActions = (
        <>
            {/* Autosave status — always visible. Rotating sync while saving,
                cloud-outline + filled checkmark once saved (or idle). */}
            <div className={styles.headerSyncStatus} aria-live="polite">
                {autosaveStatus === 'saving' ? (
                    <>
                        <SyncRoundedIcon className={clsx(styles.syncIcon, styles.syncIconSpin)} fontSize="small" />
                        <span>Saving</span>
                    </>
                ) : (
                    <>
                        <svg viewBox="-1 -1 26 26" className={styles.syncIcon} aria-hidden fill="none">
                            <path
                                stroke="currentColor"
                                strokeWidth="1.5"
                                d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
                            />
                            <path fill="currentColor" d="M10 14.18L7.91 12.09L6.5 13.5L10 17L16.01 10.99L14.60 9.58Z" />
                        </svg>
                        <span>Saved</span>
                    </>
                )}
            </div>
            {/* Last edit timestamp lives in the footer (see `.footerCenter`
                below). Figma 317:10363 placed it in the header alongside the
                actions, but the team preferred the footer placement — kept here
                as a marker in case we revisit. */}
            <button
                type="button"
                className={clsx(styles.iconBtn, commentsActive && styles.iconBtnActive)}
                aria-label="Toggle comments"
                title="Comments"
                onClick={toggleComments}
                disabled={!assesseeOrgId}
            >
                <img src={commentsIcon} alt="" aria-hidden className={styles.btnIcon} />
            </button>
            <button
                type="button"
                className={clsx(styles.historyBtn, history.isOpen && styles.historyBtnActive)}
                aria-label="View history"
                title="Version history"
                onClick={() => history.handleToggleHistory()}
                disabled={!history}
            >
                <img src={ClockCounterDark} alt="" aria-hidden className={styles.btnIcon} />
            </button>
            <div className={styles.exportGroup} ref={exportMenuRef}>
                <button
                    type="button"
                    className={clsx(styles.exportBtn, exportProgress.isRunning && styles.exportBtnExporting)}
                    onClick={handleExportServer}
                    disabled={isLoading || exporting}
                    aria-label="Export as PDF"
                    title="Export as a PDF"
                >
                    {exportProgress.isRunning ? <ExportingLabel percent={exportProgress.percent} /> : <span>Export .pdf</span>}
                </button>
                <button
                    type="button"
                    className={styles.exportCaretBtn}
                    onClick={() => setExportMenuOpen((o) => !o)}
                    disabled={isLoading || exporting}
                    aria-label="Export options"
                    aria-haspopup="true"
                    aria-expanded={exportMenuOpen}
                    title="Other export formats"
                >
                    <img src={PURPLE_DROPDOWN_CARET} alt="" aria-hidden className={styles.exportCaret} />
                </button>
                {exportMenuOpen && (
                    <div className={styles.exportPopover} role="menu" aria-label="Export options">
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={handleExportServer}
                            disabled={isLoading || exporting}
                            title="Export as a PDF"
                        >
                            Export as .pdf
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            className={styles.exportPopoverItem}
                            onClick={handleExportDocx}
                            disabled={isLoading || exporting}
                        >
                            Export as .docx
                        </button>
                    </div>
                )}
                {exportProgress.isRunning && <ProgressBorder percent={exportProgress.percent} />}
            </div>
            <Button
                text="Save & Close"
                variant="primary"
                onClick={handleSaveAndClose}
                className={styles.headerSaveBtn}
                loading={saving}
                disabled={isLoading || saving || history.isOpen}
            />
        </>
    );

    // Pinned bar shown at the bottom of the HISTORY SIDEBAR (not the editor
    // drawer's footer) when the user is previewing a non-latest entry. Just
    // the "Restore this version" button — the "Last edited on … by …"
    // metadata was redundant with the row in the sidebar that the user just
    // clicked, so it was dropped.
    const selectedHistoryIndex =
        history.isOpen && history.selectedHistoryEntryId
            ? history.historyEntries.findIndex((e) => e._id === history.selectedHistoryEntryId)
            : -1;
    const historyFooter =
        selectedHistoryIndex > 0 ? (
            <div className={styles.historyFooter}>
                <Button
                    text="Restore this version"
                    variant="primary"
                    onClick={handleRestoreClick}
                    style={{ marginTop: 0, width: '100%' }}
                    loading={history.previewLoading}
                />
            </div>
        ) : null;

    return (
        <>
            <BottomDrawer
                open={openModal}
                onClose={handleCloseWithSave}
                title={headerTitle}
                id="deal-summary-plate"
                headerRight={headerActions}
                height="100vh"
                className={styles.drawer}
                bodyClassName={styles.body}
                closingBusy={attachingToDeal}
                hideCloseBtn={commentsActive || history.isOpen}
                rightSlot={
                    <>
                        <aside className={clsx(styles.historyPane, history.isOpen && styles.historyPaneOpen)} aria-hidden={!history.isOpen}>
                            <div id={HISTORY_PORTAL_TARGET_ID} className={styles.historyPortalTarget} />
                            {historyFooter && <div className={styles.historyPaneFooter}>{historyFooter}</div>}
                        </aside>
                        {/* Comments pane lives beside the history pane in the drawer's
                            rightSlot so both span the FULL drawer height. Inside
                            `.editorRow` it was clipped to the band between the toolbar
                            host and the footer, so it read shorter than history.
                            CommentsThread portals into `#onepager-comment-container` (COMMENTS_PORTAL_TARGET_ID). */}
                        <aside
                            className={clsx(
                                styles.commentsPane,
                                commentsActive && styles.commentsPaneOpen,
                                commentsExpanded && styles.commentsPaneExpanded,
                            )}
                            aria-hidden={!commentsActive}
                        >
                            <div id={COMMENTS_PORTAL_TARGET_ID} className={styles.commentsPortalTarget} />
                        </aside>
                    </>
                }
            >
                {/* Plate toolbar host — PlateEditor portals its toolbar here so
                    it sits BELOW the drawer header bar and ABOVE the gray page
                    viewport, matching Google Docs' format-bar placement. The
                    host stays mounted (even while loading) so the editor's
                    portal target is always present on render.

                    DOM id is read by `PlateEditor` via the `toolbarHostId` prop.
                    If you rename it, update the prop value below too. */}
                <div className={styles.toolbarHost} id={PLATE_TOOLBAR_HOST_ID} />
                {/* Editor canvas. The history + comments panes live in the
                    BottomDrawer's `rightSlot` (see above), so they each animate
                    width 0 → 360px and push this whole column left when open —
                    spanning the full drawer height rather than just this row. */}
                <div className={styles.editorRow}>
                    <div className={styles.canvas} ref={canvasRef}>
                        {isLoading ? (
                            <div className={styles.loadingPane}>
                                <LoadingSpinner color={generating ? 'var(--primary-purple, #6021b3)' : undefined} />
                                <div className={styles.loadingHint}>{generating ? 'Generating one-pager…' : 'Loading template…'}</div>
                            </div>
                        ) : (
                            <PlateEditor
                                key={history.editorKey}
                                initialValue={initialValue}
                                className={styles.editor}
                                onChange={handlePlateChange}
                                readOnly={history.isOpen}
                                fullHeight
                                toolbarHostId={PLATE_TOOLBAR_HOST_ID}
                                enableAI={Boolean(aiEnabled.write)}
                                aiOrgId={assesseeOrgId}
                                showPageGaps
                                reservedPageTopPx={logoReservedPx}
                                pnoLogoUrl={pnoLogoUrl}
                                editorBandEnabled={logoEditorBandEnabled}
                            />
                        )}
                    </div>
                </div>
                {/* Bottom footer — word count on the left, last-edit text in
                    the middle (moved from the header), Regenerate as a labelled
                    button on the right (moved from the header). Stays in DOM
                    even while loading so the layout doesn't jump when the
                    editor finishes initializing. */}
                <div className={styles.footer}>
                    <div className={styles.footerLeft}>
                        <span className={styles.wordCount}>
                            {wordCount.toLocaleString()} word{wordCount === 1 ? '' : 's'}
                        </span>
                    </div>
                    <div className={styles.footerCenter}>
                        {formattedLastSaved && (
                            <span className={styles.lastEdit}>
                                Last edit: {formattedLastSaved}
                                {lastSavedBy ? ` by ${lastSavedBy}` : ''}
                            </span>
                        )}
                    </div>
                    {!history.isOpen && (
                        <div className={styles.footerRight}>
                            <button
                                type="button"
                                className={styles.regenerateBtn}
                                onClick={handleRegenerate}
                                disabled={!setShowRegenConfirmModal || isLoading}
                                aria-label="Regenerate one-pager"
                                title="Regenerate one-pager"
                            >
                                <img src={regenerateIcon} alt="" aria-hidden height="24" />
                                <span>Regenerate</span>
                            </button>
                            <PreviewButton
                                getTarget={() => canvasRef.current}
                                filename={safeExportName()}
                                disabled={isLoading}
                                injectModelPageBreaks
                                withPnoLogo
                                className={styles.footerPreviewBtn}
                            />
                        </div>
                    )}
                </div>
            </BottomDrawer>
            <HistorySideDrawer
                containerId={HISTORY_PORTAL_TARGET_ID}
                isOpen={history.isOpen}
                isExpanded={history.isExpanded}
                title="Version history"
                loading={history.loading}
                historyEntries={history.historyEntries}
                onClose={history.closeDrawer}
                onToggleExpand={history.toggleExpand}
                onSelectEntry={history.selectHistoryEntry}
                onPreviewEntry={history.previewHistoryEntry}
                selectedEntryId={history.selectedHistoryEntryId}
                previewLoading={history.previewLoading}
                onRestoreClick={handleRestoreClick}
                onRestoreConfirm={handleRestoreConfirmTracking}
            />
            {showExternalRestoreModal && history.selectedHistoryEntryId && (
                <Modal
                    open={showExternalRestoreModal}
                    onClose={() => setShowExternalRestoreModal(false)}
                    size="flexible"
                    className={styles.restoreModalOverDrawer}
                >
                    <div className={styles.restoreModal}>
                        <div className={styles.restoreTitle}>Restore this version?</div>
                        <div className={styles.restoreSubtext}>
                            Are you sure you want to restore the version from{' '}
                            {formatDateByFormat(
                                history.historyEntries.find((entry) => entry._id === history.selectedHistoryEntryId)?.lastUpdatedAt,
                                DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY,
                            )}
                            ?
                        </div>
                        <div className={styles.restoreButtons}>
                            <Button
                                text="Cancel"
                                onClick={() => setShowExternalRestoreModal(false)}
                                variant="purpleinverted"
                                style={{ marginTop: 0 }}
                            />
                            <Button
                                text="Restore"
                                onClick={handleConfirmExternalRestore}
                                variant="primary"
                                style={{ marginTop: 0 }}
                                loading={history.previewLoading}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
}
